import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { EQuestionType } from '@hubertai/screening-question-types';
import { formReducer } from './question.form.reducer';
import { questionGeneralOtherSliceReducer } from './question.general.other.reducer';
import { questionGeneralReducer } from './question.general.reducer';
import { questionIntentsReducer } from './question.intents.reducer';
import { questionOptionsReducer } from './question.options.reducer';
import { questionScopeReducer } from './question.scope.reducer';
import { questionTextReducer } from './question.text.reducer';
import { questionSlotsReducer } from './question.slots.reducer';
import { questionObjectivesReducer } from './question.objectives.reducer';
import { questionRulesReducer } from './question.rules.reducer';
import type { TextDataWithValueIds } from 'types/questions/question.types.v4';
import { ECreateQuestionMode } from 'components/Questionsv2/builder/context/create-question.context';
import type { TCreateQuestion } from 'components/Questionsv2/builder/context/create-question.context';

export type QuestionEditorState = TCreateQuestion & {
  stateModified: boolean;
  mode: ECreateQuestionMode;
  isReadOnly: boolean;
  isPublishedOrNonExclusiveQuestion: boolean;
  hasQuestionUpdatePermission: boolean;
  previewRefreshKey: number;
  formErrors?: string[];
  intentErrors?: string[];
  optionsErrors?: string[];
  optionWarnings?: string[];
  inTemplateEditAndUneditable: boolean;
  intros?: TextDataWithValueIds;
  texts?: TextDataWithValueIds;
  outros?: TextDataWithValueIds;
  clarifies?: TextDataWithValueIds;
  questionConfig?: any;
  copiedIdMap?: Record<string, string>;
};

export type QuestionEditorsState = {
  byId: Record<string, QuestionEditorState>;
  allIds: string[];
};

export const initialQuestionState: QuestionEditorState = {
  _id: undefined,
  id: '',
  version: 4,
  type: EQuestionType.SingleSelect,
  short_description: '',
  language: 'en-gb',
  availableLanguages: [],
  source_question_id: undefined,
  presentation: { block: '', label: { 'en-gb': '' } },
  stateModified: false,
  mode: ECreateQuestionMode.Create,
  isReadOnly: false,
  isPublishedOrNonExclusiveQuestion: false,
  hasQuestionUpdatePermission: true,
  previewRefreshKey: 0,
  pre_actions: [],
  post_actions: [],
  forms: [],
  intents: [],
  options: [],
  rules: [],
  visibility: 'client',
  intro: { isDefault: true, value: [] },
  text: { isDefault: true, value: [] },
  outro: { isDefault: true, value: [] },
  clarify: { isDefault: true, value: [] },
  plain_text: {},
  inTemplateEditAndUneditable: false,
  send_data_to_looker: false,
};

const initialState: QuestionEditorsState = {
  byId: {},
  allIds: [],
};

const questionEditorSlice = createSlice({
  name: 'questionEditor',
  initialState,
  reducers: {
    // Initialize from server
    setData: (state, action: PayloadAction<QuestionEditorState>) => {
      const questionData = action.payload;
      const questionId = questionData.id;

      state.byId[questionId] = questionData;
      if (!state.allIds.includes(questionId)) {
        state.allIds.push(questionId);
      }
    },
    removeQuestionEditor: (state, action: PayloadAction<string>) => {
      const questionId = action.payload;

      // Remove from byId
      delete state.byId[questionId];

      // Remove from allIds
      state.allIds = state.allIds.filter((id) => id !== questionId);
    },
    ...formReducer,
    ...questionGeneralOtherSliceReducer,
    ...questionGeneralReducer,
    ...questionIntentsReducer,
    ...questionOptionsReducer,
    ...questionScopeReducer,
    ...questionTextReducer,
    ...questionSlotsReducer,
    ...questionObjectivesReducer,
    ...questionRulesReducer,
  },
});

export const {
  setData,
  removeQuestionEditor,
  // forms
  resetForm,
  dragAndDropForm,
  addForm,
  removeForm,
  updateFormHubiverseProperty,
  updateFormField,
  addMatrixRowByIndex,
  addMatrixColumnByIndex,
  removeMatrixColumnByIndex,
  removeMatrixRowByIndex,
  updateMatrixRowColumnByIndex,

  //general other
  setIsReadOnly,
  resetGeneralOther,
  setRefreshPreview,
  updateDefaultScoringCategory,
  updateDefaultWeight,
  updatePostActions,
  updatePreActions,
  updateHelpText,
  updateSendDataToLooker,
  updateDeferrable,
  updateRandomizeOrder,
  updateTitle,
  updateMinRequired,
  updateRankingMode,
  updateDisplayMode,
  updateMaxDuration,
  updateMinDuration,
  updateAllowRetake,
  updateMaxRetakes,
  updateMaxThinkingSeconds,
  updateRecordingRequired,
  updateRecordingQuality,
  updateSchedulingConfig,
  updateSchedulingReplies,

  // general
  resetGeneral,
  updateType,
  updateShortDescription,
  updateLanguage,
  updateConversationSettings,
  updateVoiceSettings,
  updatePresentation,
  markAsSaved,

  //intents
  resetIntents,
  addReply,
  removeReply,
  editReply,
  addIntent,
  removeIntent,
  updateIntent,
  updateIntentEnabled,

  //options
  resetOptions,
  clearOptions,
  addOption,
  addOptions,
  updateOption,
  moveOption,
  toggleOptionEnabled,
  removeOptionById,
  addStaticOptionAction,
  addActionToOption,
  removeActionFromOption,
  updateActionInOption,
  dragAndDropOption,
  removeReplyOptions,
  editReplyOptions,
  addReplyOptions,
  updateMinSelection,
  updateMaxSelection,

  //scope
  resetScope,
  updateVisibility,
  updateClientId,
  updateAccessGroupIds,

  //text
  resetText,
  setText,
  addText,
  editText,
  removeText,
  moveText,
  dragAndDropText,

  //slots
  addSlot,
  removeSlot,
  updateSlot,
  addSlotOption,
  updateSlotOption,
  removeSlotOption,
  updateSlotFilling,

  //objectives
  addObjective,
  removeObjective,
  updateObjective,
  updateObjectives,

  //rules
  upsertQuestionRule,
  removeQuestionRule,
} = questionEditorSlice.actions;

export default questionEditorSlice.reducer;
